import XResizableTextarea from '../common/XResizableTextarea';
import XButton from '../common/XButton';
import useLifecareStudioComment from '../../hooks/useLifecareStudioComment';
import { cn } from '../../utils/classNames';

const wrapperClasses =
  'px-4 py-3 ' +
  'bg-white border border-border rounded-2xl ' +
  'shadow-[0_0.75rem_2rem_-0.75rem_rgba(0,0,0,0.25),0_0.25rem_0.75rem_-0.25rem_rgba(0,0,0,0.08)] ' +
  'transition-colors';

const wrapperStateClasses = {
  success: 'border-success! ring-2 ring-success/40 bg-success/[0.04]',
  failed: 'border-danger! ring-2 ring-danger/40 bg-danger-surface',
};

const rowClasses = 'flex items-center gap-3';

const textareaWrapClasses = 'flex-1 min-w-0';

// max-h-40 caps growth so a long comment scrolls internally instead of stretching the bar.
const textareaClasses =
  'w-full font-secondary text-sm leading-5 text-content ' +
  'bg-transparent border-0! p-0 resize-none ' +
  'focus:outline-none focus:ring-0! focus:border-0! ' +
  'placeholder:text-subtle max-h-40 overflow-y-auto!';

const buttonWrapClasses = 'shrink-0 flex items-center gap-2';

const regenerateButtonClasses = `
  h-9! py-0! px-3!
  rounded-lg! text-sm! font-medium!
  border-border! text-subtle!
  hover:not-disabled:border-primary! hover:not-disabled:text-primary!
  hover:not-disabled:bg-primary/[0.08]!
`;

const inlineSpinnerClasses =
  'inline-block align-[-0.125em] animate-spin h-4 w-4 ' +
  'border-2 border-current border-t-transparent rounded-full text-primary';

const statusBadgeClasses = {
  success:
    'inline-flex items-center gap-1.5 text-sm font-medium text-success',
  failed:
    'inline-flex items-center gap-1.5 text-sm font-medium text-danger',
};

const hintWrapperClasses = 'flex items-center justify-center';
const hintClasses = 'm-0 text-sm text-subtle italic text-center';

const LifecareStudioCommentBar = ({
  activeTab,
  section,
  uiState,
  updateSectionComment,
  handleRegenerateSection,
  disabled = false,
}) => {
  const { label, name, value, onChange, onRegenerate } = useLifecareStudioComment({
    section,
    uiState,
    updateSectionComment,
    handleRegenerateSection,
  });

  if (!activeTab) {
    return (
      <div className={cn(wrapperClasses, hintWrapperClasses)}>
        <p className={hintClasses}>
          Select a client to leave a section comment.
        </p>
      </div>
    );
  }

  if (!activeTab.commentField) {
    return (
      <div className={cn(wrapperClasses, hintWrapperClasses)}>
        <p className={hintClasses}>
          Summary totals are computed from the Future Care Needs rows. Switch tabs
          to leave a section comment.
        </p>
      </div>
    );
  }

  if (!section) {
    return (
      <div className={cn(wrapperClasses, hintWrapperClasses)}>
        <p className={hintClasses}>
          Select a client to leave a section comment.
        </p>
      </div>
    );
  }

  const stateClass = wrapperStateClasses[uiState] || '';

  return (
    <div className={cn(wrapperClasses, stateClass)}>
      <div className={rowClasses}>
        <div className={textareaWrapClasses}>
          <XResizableTextarea
            name={name}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Describe what to change in ${label}`}
            className={textareaClasses}
            minHeight="1.25rem"
            disabled={disabled}
          />
        </div>
        <div className={buttonWrapClasses}>
          {uiState === 'success' && (
            <span className={statusBadgeClasses.success} role="status">
              <i className="fa fa-check-circle" />
              Done
            </span>
          )}
          {uiState === 'failed' && (
            <span className={statusBadgeClasses.failed} role="status">
              <i className="fa fa-exclamation-circle" />
              Failed
            </span>
          )}
          {uiState === 'loading' ? (
            <output className="flex items-center gap-2 text-sm text-primary">
              <span className={inlineSpinnerClasses} />
              <span>Regenerating...</span>
            </output>
          ) : (
            <XButton
              variant="outline"
              className={regenerateButtonClasses}
              onClick={onRegenerate}
              disabled={disabled || !value?.trim()}
            >
              <i className="fa fa-refresh" />
              <span>Regenerate</span>
            </XButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default LifecareStudioCommentBar;
